
/**
 * @fileoverview Hook de Data Layer para inicialización de datos globales del inbox.
 * Realiza la carga de agentes, tags y chats no leídos al montar el componente.
 * @module useInboxData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 2.1.0
 */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchInboxAgentsIfEmpty } from '../../../../actions/inbox/retailerUserActions';
import { getTagsIfEmpty } from '../../../../actions/inbox/tagActions';
import { getUnreadChats } from '../../../../actions/inbox/conversationActions';

/**
 * Hook de Data Layer para inicialización de datos globales del inbox.
 *
 * Implementa el patrón de Data Layer con separación estricta de responsabilidades:
 * - Solo gestiona la carga de datos globales al montar el inbox
 * - No gestiona lógica de UI ni validaciones de negocio
 * - Optimizado con useEffect para evitar cargas innecesarias
 *
 * Flujo de operación:
 * 1. Al montar, despacha acciones para cargar tags, agentes y chats no leídos si están vacíos
 * 2. No retorna datos, solo ejecuta efectos secundarios
 *
 * @function useInboxData
 * @category Hook - Data Layer
 * @returns {void}
 * @see fetchInboxAgentsIfEmpty
 * @see getTagsIfEmpty
 * @see getUnreadChats
 * @since 1.0.0
 * @version 2.1.0 - Optimización de performance y documentación
 */
export default function useInboxData() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTagsIfEmpty());
    dispatch(fetchInboxAgentsIfEmpty());
    dispatch(getUnreadChats());
  }, [dispatch]);
}
